import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingProgressModal = ({ 
  isOpen, 
  progress = 0, 
  currentAsset = '', 
  totalAssets = 0, 
  loadedAssets = 0,
  stage = 'initializing' // 'initializing', 'loading-assets', 'finalizing'
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);

  // Smooth progress animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayProgress(progress);
    }, 100);
    return () => clearTimeout(timer);
  }, [progress]);

  // Get stage message
  const getStageMessage = () => {
    switch (stage) {
      case 'initializing':
        return 'Initializing project...';
      case 'loading-assets':
        return `Loading assets (${loadedAssets}/${totalAssets})`;
      case 'finalizing':
        return 'Finalizing project setup...';
      default:
        return 'Loading...';
    }
  };

  // Get progress bar color based on progress
  const getProgressColor = () => {
    if (displayProgress < 30) return 'bg-red-500';
    if (displayProgress < 60) return 'bg-yellow-500';
    if (displayProgress < 90) return 'bg-blue-500';
    return 'bg-ag-green';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            className="bg-ag-black-700 rounded-lg p-8 max-w-md w-full mx-4 border border-ag-black-400 shadow-2xl"
          >
            {/* Logo or Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-ag-green/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-ag-green animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-white text-center mb-2">
              Opening Project
            </h2>

            {/* Stage Message */}
            <p className="text-gray-400 text-center mb-6">
              {getStageMessage()}
            </p>

            {/* Progress Bar Container */}
            <div className="mb-4">
              <div className="bg-ag-black-800 rounded-full h-3 overflow-hidden border border-ag-black-400">
                <motion.div
                  className={`h-full ${getProgressColor()} transition-all duration-500 ease-out relative overflow-hidden`}
                  initial={{ width: 0 }}
                  animate={{ width: `${displayProgress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                </motion.div>
              </div>
            </div>

            {/* Progress Percentage */}
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-500">Progress</span>
              <span className="text-sm font-medium text-white">
                {Math.round(displayProgress)}%
              </span>
            </div>

            {/* Current Asset Being Loaded */}
            {currentAsset && (
              <div className="bg-ag-black-800 rounded-md p-3 border border-ag-black-400">
                <p className="text-xs text-gray-500 mb-1">Currently loading:</p>
                <p className="text-sm text-gray-300 truncate font-mono">
                  {currentAsset}
                </p>
              </div>
            )}

            {/* Loading Dots Animation */}
            <div className="flex justify-center mt-6 space-x-2">
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 h-2 bg-ag-green rounded-full"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                />
              ))}
            </div>

            {/* Warning for slow loading */}
            {displayProgress > 0 && displayProgress < 100 && stage === 'loading-assets' && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-gray-500 text-center mt-4"
              >
                Please wait while assets are being loaded...
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingProgressModal;